/**
 * listingVotes — the FAIR / OVER price vote posts, and toggles.
 *
 * WHAT IT DID. `[data-vote]` (app.js:4496) wrote `store.votes[id]` client-side,
 * repainted, and toasted "Vote counted · FAIR". No POST. Worse than dead: a
 * second click was REFUSED with "You already called this one", so a mistap was
 * permanent and a mind changed was not allowed. The owner asked for click and
 * unclick, the way the app does it (2026-08-28).
 *
 * The server has always supported the whole shape:
 *   POST   /api/listings/:id/vote  { vote: "up" | "down" }   (listings.ts:2262)
 *   DELETE /api/listings/:id/vote                            (listings.ts:2360)
 * A changed vote reverses the old delta and applies the new one in one
 * statement; DELETE clears it outright. Both are what mobile drives
 * (lib/listings.ts voteListing / undoListingVote), so this is the app's own
 * behaviour, not a second interpretation of it.
 *
 * 🔑 THE COUNT ARITHMETIC, which is the whole reason this file needs care.
 *
 * Blake renders the total as BASELINE + YOUR OWN VOTE:
 *   fair = l.fairVotes + (store.votes[id] === "fair" ? 1 : 0)   (app.js:187)
 * where `l.fairVotes` is `listings.upvotes` off the wire. That composes
 * correctly with the server for every action taken in this session.
 *
 * It breaks in exactly one case, and the server hands us the fix. GET
 * /api/listings (the list route installFloor uses) carries NO per-viewer state
 * — `myVote` exists only on the single-listing route (listings.ts:883). So a
 * listing you voted on YESTERDAY loads with a baseline that already counts you,
 * and the UI does not know. Tapping FAIR would then render baseline + 1: one
 * too many.
 *
 * POST answers `{ ok: true, changed: false }` for precisely that case — the
 * same vote already recorded, nothing moved. So `changed === false` means the
 * baseline already includes you, and dropping it by one lands the render on the
 * server's true total. Every other transition needs no adjustment:
 *
 *   first vote        changed:true   baseline excludes you, +1 is correct
 *   same vote again   changed:FALSE  baseline includes you  -> baseline -= 1
 *   fair -> over      changed:true   your +1 simply moves sides
 *   unvote (DELETE)   changed:true   your +1 goes away with store.votes[id]
 *
 * 📌 WHAT THIS STILL CANNOT DO, stated rather than hidden: on a FRESH page load
 * your existing votes do not paint as selected, because the feed does not carry
 * them. The fix is one field on the list route and it is the server's to add.
 * Reconstructing it from localStorage would be a guess presented as fact, which
 * is the class of thing this whole lane exists to remove.
 */

import { signedInOrWall } from "./guestWall";

/** His control, on the card (app.js:218) and the detail page (app.js:918). */
export const VOTE_SELECTOR = "[data-vote]";

/** His two values, and the wire's. They are not the same words. */
export const FAIR = "fair";
export const OVER = "over";

/** Reused from the other interceptors, which face the same 401. */
export const SIGNIN_MESSAGE = "Sign in to vote on a price";
/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Vote failed — please try again";

interface VoteWindow extends Window {
  /** Bridge: the viewer's own vote, and the row's cached counts. */
  __gbzSetListingVote?: (
    id: string,
    vote: string | null,
    fairDelta: number,
    overDelta: number,
  ) => boolean;
  /** Bridge: the viewer's current vote for this listing, or null. */
  __gbzListingVote?: (id: string) => string | null;
  rerenderKeepScroll?: () => void;
  render?: () => void;
  toast?: (message: string) => void;
}

/** His word -> the wire's word. */
function wireVote(blakeVote: string): "up" | "down" | null {
  if (blakeVote === FAIR) return "up";
  if (blakeVote === OVER) return "down";
  return null;
}

export function installListingVotes(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      // #toast only exists after the first render; degrade to silence rather
      // than throwing inside a capture listener.
      if (!win.document.getElementById("toast")) return;
      (win as VoteWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  /* In flight, per listing. His handler had no re-entrancy guard because it was
     synchronous; with a real await a double-tap is two requests racing, and the
     second can land on a baseline the first has already moved. */
  const busy = new Set<string>();

  function repaint(): void {
    const target = win as VoteWindow;
    /* rerenderKeepScroll, not render: these controls sit on cards halfway down
       the feed. A top-level function declaration in app.js, so it is a real
       window property (floorTransport uses it the same way). */
    if (typeof target.rerenderKeepScroll === "function") target.rerenderKeepScroll();
    else target.render?.();
  }

  async function cast(listingId: string, blakeVote: string): Promise<void> {
    const target = win as VoteWindow;
    const current = target.__gbzListingVote?.(listingId) ?? null;

    /* TAPPING YOUR OWN VOTE CLEARS IT. This is the half his handler refused
       outright, and the owner asked for it by name. */
    if (current === blakeVote) {
      const res = await authFetch(`/api/listings/${encodeURIComponent(listingId)}/vote`, {
        method: "DELETE",
      });
      if (res.status === 401) { notify(SIGNIN_MESSAGE); return; }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        notify(body.error ?? GENERIC_FAILURE_MESSAGE);
        return;
      }
      /* No baseline change: the +1 that leaves with store.votes[id] is exactly
         the one the server just removed. A `changed:false` here means there was
         nothing recorded to remove, which the local clear already matches. */
      target.__gbzSetListingVote?.(listingId, null, 0, 0);
      repaint();
      return;
    }

    const wire = wireVote(blakeVote);
    if (!wire) return;

    const res = await authFetch(`/api/listings/${encodeURIComponent(listingId)}/vote`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vote: wire }),
    });
    if (res.status === 401) { notify(SIGNIN_MESSAGE); return; }
    if (!res.ok) {
      /* 409 is "Voting is closed on this listing" — the listing left an
         engageable status. The server's words, not ours. */
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      notify(body.error ?? GENERIC_FAILURE_MESSAGE);
      return;
    }

    const body = (await res.json().catch(() => ({}))) as { changed?: boolean };
    /* THE ONE ADJUSTMENT. changed:false means the server already held this exact
       vote, so the baseline already counts us and Blake's +1 would double it.
       See the header for why this is the only transition that needs it. */
    const alreadyCounted = body.changed === false;
    const fairDelta = alreadyCounted && blakeVote === FAIR ? -1 : 0;
    const overDelta = alreadyCounted && blakeVote === OVER ? -1 : 0;

    target.__gbzSetListingVote?.(listingId, blakeVote, fairDelta, overDelta);
    repaint();
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const el = target.closest(VOTE_SELECTOR) as HTMLElement | null;
    if (!el) return;

    const listingId = el.dataset.id;
    const blakeVote = el.dataset.vote;
    if (!listingId || !blakeVote) return;
    // A guest gets the sign-in wall and no request — see guestWall.ts.
    if (!signedInOrWall(win)) return;
    if (busy.has(listingId)) return;

    busy.add(listingId);
    void cast(listingId, blakeVote)
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => busy.delete(listingId));
  };

  /* Capture on the document, same posture as the other interceptors: his
     listener is bound on the element in the target phase, so this runs first.

     🔴 `[data-vote]` IS IN GUARDED_WRITES, and it has to be. Letting his
     handler run alongside this one is NOT harmless the way it is for the
     publish button: his toasts "Vote counted · FAIR" synchronously, before
     our request has resolved, so a vote the server refuses has already been
     announced as counted. And on the unvote tap he toasts "You already called
     this one" — the refusal this change exists to remove. The guard kills
     both.

     This does NOT stop propagation itself, so the guard still fires and stays
     the fail-safe: if this module ever fails to install, the control goes back
     to dead rather than to claiming a vote it did not cast.

     📌 His own stopPropagation is not load-bearing for card-open suppression:
     the card handler already excludes [data-vote] by target check
     (app.js:4246), so guarding the control cannot make a vote tap open the
     listing. Checked, not assumed. */
  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
